import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { fetchAffiliateProgram, type AffiliateProgram } from "./affiliate";
import { api, apiBlob, apiUpload } from "./api";
import { track } from "./gtm";
import { PRESET_CRONS } from "./cron";
import type {
  BorderPayMethods,
  OrgQuota,
  QuotaRequest,
  QuotaResource,
  DomainAvailability,
  DomainContact,
  DomainContactInput,
  DomainOrder,
  DomainTld,
  RegisteredDomain,
  RegisteredDomainDetail,
  RegisteredDomainList,
  ZoneRecord,
  AccessRule,
  AccessRulesResponse,
  AiModel,
  Announcement,
  ApiKey,
  ApiKeyLimits,
  Application,
  ApplicationDetail,
  BackupFile,
  BackupSchedule,
  BillingDocument,
  BillingIssuer,
  BillingSummary,
  CreateAppPayload,
  CreateSchedulePayload,
  CreatedApiKey,
  CreatedApplication,
  CreatedDatabase,
  CreatedInvitation,
  Database,
  DatabaseConnection,
  DatabaseEngine,
  DeletedSchedule,
  Deployment,
  DeploymentLogs,
  Domain,
  EnvVar,
  GitBranch,
  GitConnection,
  GitProviderId,
  GitProviderInfo,
  GitRepo,
  InferenceUsage,
  Invitation,
  LedgerEntry,
  Member,
  MemberRole,
  Organization,
  OrganizationDetail,
  OrganizationInput,
  Paginated,
  Payment,
  Plan,
  EgressCatalogue,
  RegionCatalogue,
  Tunnel,
  TunnelConnectivity,
  TunnelDetail,
  TunnelList,
  TunnelPeerInput,
  TunnelTarget,
  PromoRedemption,
  RuntimeReport,
  Schedule,
  ScheduleDetail,
  ScheduleList,
  ScheduleRunList,
  ScheduleRunLogs,
  SpendReport,
  SupportAttachment,
  SupportCategory,
  SupportMessage,
  SupportPriority,
  SupportStatus,
  SupportTicket,
  SupportTicketDetail,
  UpdateSchedulePayload,
  UsageDay,
  ScaleEvent,
  ScaleRule,
  DatabaseRestore,
} from "./types";

/*
 * Milestones are measured here rather than in the components that trigger them.
 *
 * Every mutation in the dashboard lives in this file, so this is the one place
 * where "what did somebody actually do" is knowable without reading forty
 * screens — and the one place a new mutation is written, which is what keeps
 * the next milestone from shipping unmeasured. Instrumenting the buttons
 * instead would have meant the same event pushed from three call sites, each
 * with its own idea of what to call it.
 *
 * A mutation's own `onSuccess` does not displace the caller's: React Query runs
 * both, so nothing below changes what a screen does when a request succeeds.
 *
 * Only milestones. Reads are not events, and neither is every write — renaming
 * an app is not a fact anybody will read a report about, while creating one,
 * deploying it and deleting it are the shape of the product's funnel.
 */
const base = (orgId: string) => `/v1/orgs/${orgId}`;
const appBase = (orgId: string, appId: string) => `${base(orgId)}/apps/${appId}`;

export const keys = {
  org: (orgId: string) => ["org", orgId] as const,
  /** Not org-scoped: the catalogue is the same for everybody. */
  aiModels: ["ai-models"] as const,
  /** Not org-scoped either: one programme, the same terms for everybody. */
  affiliateProgram: ["affiliate-program"] as const,
  /*
   * The range and both filters are part of the key, so switching range serves a
   * cached window instantly and going back to it does not refetch. Keying on the
   * org alone would make every range overwrite the last one in the cache and the
   * picker would flash a loading state each way.
   */
  inferenceUsage: (orgId: string, query: InferenceUsageQuery = {}) =>
    [
      "org",
      orgId,
      "inference",
      "usage",
      query.days ?? null,
      query.model ?? null,
      query.keyId ?? null,
    ] as const,
  apps: (orgId: string, limit?: number) =>
    limit === undefined
      ? (["org", orgId, "apps"] as const)
      : (["org", orgId, "apps", limit] as const),
  app: (orgId: string, appId: string) => ["org", orgId, "apps", appId] as const,
  /*
   * `limit` is part of the key so each page is its own entry, and optional so
   * an invalidation can omit it and match every page by prefix — which is what
   * a deploy, a cancel or a lifecycle change wants.
   */
  deployments: (orgId: string, appId: string, limit?: number) =>
    limit === undefined
      ? (["org", orgId, "apps", appId, "deployments"] as const)
      : (["org", orgId, "apps", appId, "deployments", limit] as const),
  logs: (orgId: string, appId: string, deploymentId: string) =>
    ["org", orgId, "apps", appId, "deployments", deploymentId, "logs"] as const,
  env: (orgId: string, appId: string) => ["org", orgId, "apps", appId, "env"] as const,
  domains: (orgId: string, appId: string) => ["org", orgId, "apps", appId, "domains"] as const,
  /*
   * Not app-scoped, because the Schedules page is org-wide and the per-app tab
   * is the same list filtered. Keying by app would make the tab a second cache
   * entry that the page's own invalidation could not reach.
   */
  schedules: (orgId: string, appId?: string) =>
    appId === undefined
      ? (["org", orgId, "schedules"] as const)
      : (["org", orgId, "schedules", appId] as const),
  schedule: (orgId: string, scheduleId: string) =>
    ["org", orgId, "schedules", "detail", scheduleId] as const,
  /** `limit` in the key for the same reason deployments carry one: pages, not slices. */
  /*
   * The page is in the key, and optional so an invalidation can omit it and
   * match every page by prefix.
   */
  scheduleRuns: (orgId: string, scheduleId: string, page?: { limit: number; offset: number }) =>
    page === undefined
      ? (["org", orgId, "schedules", "detail", scheduleId, "runs"] as const)
      : ([
          "org",
          orgId,
          "schedules",
          "detail",
          scheduleId,
          "runs",
          page.limit,
          page.offset,
        ] as const),
  scheduleRunLogs: (orgId: string, scheduleId: string, runId: string) =>
    ["org", orgId, "schedules", "detail", scheduleId, "runs", runId, "logs"] as const,
  members: (orgId: string) => ["org", orgId, "members"] as const,
  invitations: (orgId: string) => ["org", orgId, "invitations"] as const,
  apiKeys: (orgId: string) => ["org", orgId, "api-keys"] as const,
  /** One page of one state. Prefixed by `apiKeys`, so invalidating that clears every page. */
  quota: (orgId: string) => ["quota", orgId] as const,
  apiKeyPage: (orgId: string, state: ApiKeyState, limit: number) =>
    ["org", orgId, "api-keys", state, limit] as const,
  plans: (orgId: string, kind: string) => ["org", orgId, "plans", kind] as const,
  regions: (orgId: string) => ["org", orgId, "regions"] as const,
  egress: (orgId: string) => ["org", orgId, "egress"] as const,
  tunnels: (orgId: string) => ["org", orgId, "tunnels"] as const,
  /* "registered", to keep clear of `domains` above, which is an app's custom
     hostnames. These are names the platform holds for the organization. */
  registeredDomains: (orgId: string) => ["org", orgId, "registered-domains"] as const,
  registeredDomain: (orgId: string, domainId: string) =>
    ["org", orgId, "registered-domains", domainId] as const,
  domainContact: (orgId: string) => ["org", orgId, "domain-contact"] as const,
  domainRecords: (orgId: string, domainId: string) =>
    ["org", orgId, "registered-domains", domainId, "records"] as const,
  tunnel: (orgId: string, tunnelId: string) => ["org", orgId, "tunnels", tunnelId] as const,
  databases: (orgId: string, limit?: number) =>
    limit === undefined
      ? (["org", orgId, "databases"] as const)
      : (["org", orgId, "databases", limit] as const),
  appRuntime: (orgId: string, appId: string) =>
    ["org", orgId, "apps", appId, "runtime"] as const,
  appLogs: (orgId: string, appId: string) => ["org", orgId, "apps", appId, "logs"] as const,
  databaseRuntime: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "runtime"] as const,
  databaseLogs: (orgId: string, id: string, options: unknown) =>
    ["org", orgId, "databases", id, "logs", options] as const,
  backupSchedule: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "backups"] as const,
  backupFiles: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "backups", "files"] as const,
  restores: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "backups", "restores"] as const,
  database: (orgId: string, id: string) => ["org", orgId, "databases", id] as const,
  databaseConnection: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "connection"] as const,
  accessRules: (orgId: string, id: string) =>
    ["org", orgId, "databases", id, "access-rules"] as const,
  billing: (orgId: string) => ["org", orgId, "billing"] as const,
  /* Each history keys on its own page size: growing one must not evict the
     others, and stepping back to a shorter list should be free. */
  ledger: (orgId: string, limit = 25) => ["org", orgId, "billing", "ledger", limit] as const,
  usageHistory: (orgId: string, limit = 25) =>
    ["org", orgId, "billing", "usage-history", limit] as const,
  payments: (orgId: string) => ["org", orgId, "billing", "payments"] as const,
  spend: (orgId: string) => ["org", orgId, "billing", "spend"] as const,
  documents: (orgId: string, limit = 25) =>
    ["org", orgId, "billing", "documents", limit] as const,
  gitProviders: (orgId: string) => ["org", orgId, "git", "providers"] as const,
  gitConnections: (orgId: string) => ["org", orgId, "git", "connections"] as const,
  gitRepos: (orgId: string, connectionId: string, page: number) =>
    ["org", orgId, "git", connectionId, "repos", page] as const,
  gitBranches: (orgId: string, connectionId: string, repo: string) =>
    ["org", orgId, "git", connectionId, "branches", repo] as const,
  /*
   * Keyed on the organization, or on nothing when there is not one yet.
   *
   * The two are genuinely different answers rather than one filtered: signed in,
   * the set includes notices scoped to a server this organization has something
   * on. Sharing a key would serve a signed-out visitor's platform-wide list to
   * somebody who has since signed in and is entitled to more.
   */
  announcements: (orgId: string | undefined) =>
    orgId ? (["org", orgId, "announcements"] as const) : (["announcements", "public"] as const),
  /* The status filter is part of the key, so switching tabs serves a cached
     slice instantly rather than flashing a spinner over a list of four. */
  /* Every filter and the limit are part of the key, so switching a tab serves a
     cached slice instantly and each "Show more" step is its own answer. */
  tickets: (orgId: string, query: TicketQuery = {}) =>
    [
      "org",
      orgId,
      "tickets",
      query.status ?? "all",
      query.category ?? "any",
      query.q ?? "",
      query.limit ?? 25,
    ] as const,
  ticketCounts: (orgId: string) => ["org", orgId, "tickets", "counts"] as const,
  ticket: (orgId: string, ticketId: string) => ["org", orgId, "tickets", ticketId] as const,
};

// ------------------------------------------------------------------- org

export function useOrg(orgId: string | undefined): UseQueryResult<OrganizationDetail> {
  return useQuery({
    queryKey: keys.org(orgId ?? ""),
    queryFn: () => api<OrganizationDetail>(base(orgId!)),
    enabled: Boolean(orgId),
  });
}

// ------------------------------------------------------------------ apps

/**
 * A page of applications, with how many the organization has in total.
 *
 * Paged because the route resolves each app's state against the deployment
 * engine before answering — an unbounded list was an unbounded fan-out on
 * every visit, not just a large payload.
 */
export function useApps(
  orgId: string | undefined,
  limit = 25,
): UseQueryResult<{ data: Application[]; total: number }> {
  return useQuery({
    queryKey: keys.apps(orgId ?? "", limit),
    queryFn: () =>
      api<{ data: Application[]; total: number }>(`${base(orgId!)}/apps?limit=${limit}`),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
}

export function useApp(
  orgId: string | undefined,
  appId: string | undefined,
): UseQueryResult<ApplicationDetail> {
  return useQuery({
    queryKey: keys.app(orgId ?? "", appId ?? ""),
    queryFn: () => api<ApplicationDetail>(appBase(orgId!, appId!)),
    enabled: Boolean(orgId && appId),
    // A release can change the resolved app state while this header is on
    // screen. Poll only for that short transitional period, not forever —
    // and slowly while a clean rebuild is pending, since the API clears that
    // flag after the build has finished, when this would otherwise be idle.
    refetchInterval: ({ state: { data } }) =>
      data?.status.value === "deploying" ? 5_000 : data?.cleanCachePending ? 60_000 : false,
  });
}

export function useCreateApp(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAppPayload) =>
      api<CreatedApplication>(`${base(orgId)}/apps`, { method: "POST", body: payload, idempotent: true }),
    onSuccess: (created, payload) => {
      // Where the code came from, which is the branch of the funnel worth
      // knowing. Not the repository or the image — both name somebody's work.
      track("app_create", { object: "app", id: created.id, detail: payload.source.type });
      void queryClient.invalidateQueries({ queryKey: keys.apps(orgId) });
    },
  });
}

// ---------------------------------------------------------------- billing

export function useBilling(orgId: string | undefined): UseQueryResult<BillingSummary> {
  return useQuery({
    queryKey: keys.billing(orgId ?? ""),
    queryFn: () => api<BillingSummary>(`${base(orgId!)}/billing`),
    enabled: Boolean(orgId),
    // The balance moves every hour, so a stale figure is misleading rather
    // than merely old.
    refetchInterval: 60_000,
  });
}

/**
 * Balance changes that were neither usage nor a top-up.
 *
 * Paged, and the API does the excluding: the dashboard used to ask for a
 * hundred non-usage entries and filter the top-ups out of them, so an
 * organization that had topped up a hundred times rendered an empty
 * "Adjustments" table while it had adjustments.
 */
export function useLedger(
  orgId: string | undefined,
  limit = 25,
): UseQueryResult<{ entries: LedgerEntry[]; total: number }> {
  return useQuery({
    queryKey: keys.ledger(orgId ?? "", limit),
    queryFn: () =>
      api<{ entries: LedgerEntry[]; total: number }>(`${base(orgId!)}/billing/ledger?limit=${limit}`),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Usage rolled up by day and workload.
 *
 * Its own request rather than a second list inside the ledger response, which
 * is where it used to live: two independently-paged histories in one endpoint
 * means loading more of one re-runs the other.
 *
 * A row is a day *and* a workload, so this grows several rows a day for a
 * tenant running several things — which is why it is paged rather than capped.
 */
export function useUsageHistory(
  orgId: string | undefined,
  limit = 25,
): UseQueryResult<{ days: UsageDay[]; total: number }> {
  return useQuery({
    queryKey: keys.usageHistory(orgId ?? "", limit),
    queryFn: () =>
      api<{ days: UsageDay[]; total: number }>(`${base(orgId!)}/billing/usage?limit=${limit}`),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Top-up attempts. Read for one thing on this page — the ones that need
 * attention — so it asks for a generous first page and does not grow: an
 * abandoned payment older than fifty top-ups is not what that card is for.
 */
export function usePayments(orgId: string | undefined, enabled: boolean): UseQueryResult<Payment[]> {
  return useQuery({
    queryKey: keys.payments(orgId ?? ""),
    queryFn: async () =>
      (await api<Paginated<Payment>>(`${base(orgId!)}/billing/payments?limit=50`)).data,
    enabled: Boolean(orgId) && enabled,
  });
}

/**
 * Starts a top-up and hands back the processor's payment URL. The browser
 * navigates there itself; a cross-origin redirect cannot be followed from
 * `fetch`.
 *
 * `provider` is optional and names one of `billing.paymentProviders`. Omitted,
 * the API picks the deployment's preferred processor — which is also what an
 * older dashboard build does, so leaving it out stays correct rather than
 * becoming a missing field.
 */
/** Only asked once BorderPay is the processor on screen: a list nobody else needs. */
export function useBorderPayMethods(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["borderpay-methods", orgId],
    queryFn: () => api<BorderPayMethods>(`${base(orgId!)}/billing/borderpay/methods`),
    enabled: Boolean(orgId) && enabled,
    staleTime: 600_000,
  });
}

export function useStartTopUp(orgId: string) {
  return useMutation({
    mutationFn: (payload: { amountCents: number; redirectTo?: string; provider?: string; method?: { kind: string; code?: string } }) =>
      api<{ paymentId: string; approveUrl: string }>(`${base(orgId)}/billing/topup`, {
        method: "POST",
        body: payload,
        // Fresh per attempt: two identical top-ups in a row are two top-ups.
        // The key still covers the 401 resend and a double submit.
        idempotent: crypto.randomUUID(),
      }),
  });
}

/**
 * Redeems a promotion code.
 *
 * Invalidates the billing summary and the ledger on success, because a grant
 * moves the balance immediately and a match changes what the page has to say
 * about the next payment. Not invalidated on failure: a refused code has changed
 * nothing, and refetching would flicker the page for no reason.
 */
export function useRedeemPromo(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: { code: string }) =>
      api<PromoRedemption>(`${base(orgId)}/billing/promo`, { method: "POST", body: payload }),
    onSuccess: (redemption) => {
      // The campaign, which is what a promotion is measured by. A code is a
      // marketing identifier rather than a personal one.
      track("redeem_promo", {
        object: "payment",
        id: redemption.campaign.code,
        detail: redemption.campaign.kind,
      });
      void client.invalidateQueries({ queryKey: keys.billing(orgId) });
      void client.invalidateQueries({ queryKey: keys.ledger(orgId) });
    },
  });
}

/** Month-to-date spend analytics. The balance moves every hour, so does this. */
export function useSpend(orgId: string | undefined): UseQueryResult<SpendReport> {
  return useQuery({
    queryKey: keys.spend(orgId ?? ""),
    queryFn: () => api<SpendReport>(`${base(orgId!)}/billing/spend`),
    enabled: Boolean(orgId),
    refetchInterval: 60_000,
  });
}

/* ------------------------------------------------------------- inference */

/**
 * The public model catalogue.
 *
 * Cached long, because it changes when an operator lists a model rather than
 * with anything the customer does — refetching it on every focus would be a
 * request per tab switch for a list that moves weekly at most.
 */
export function useAiModels(): UseQueryResult<AiModel[]> {
  return useQuery({
    queryKey: keys.aiModels,
    /* No cookie and no token. The catalogue is unauthenticated on purpose and
       identical for everybody, and the API keeps a colo-local copy of it --
       but that cache bypasses any request carrying a credential, deliberately,
       so a per-caller response can never be stored by mistake. Sending one we
       do not need turned every read into a full render: measured on UAT, 1.9s
       and a Postgres connection against 0.25s from the cache, for 578KB.

       Both flags are needed: the cache bypasses on the cookie or the bearer
       token, and dropping only one of them changes nothing. */
    queryFn: async () =>
      (await api<{ data: AiModel[] }>("/v1/models", { anonymous: true, omitCredentials: true }))
        .data,
    staleTime: 5 * 60_000,
  });
}

/**
 * The affiliate programme's terms, for the referral page.
 *
 * One attempt: the page offers the programme without them, and three retries
 * behind a backoff would hold the figures back from a page that already works.
 */
export function useAffiliateProgram(enabled: boolean): UseQueryResult<AffiliateProgram> {
  return useQuery({
    queryKey: keys.affiliateProgram,
    // No abort signal. The page mounts twice as the shell settles, and a query
    // that consumed one is cancelled on the first unmount and sent again.
    queryFn: () => fetchAffiliateProgram(),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface InferenceUsageQuery {
  /** One of the ranges the API allows. Anything else falls back to its default. */
  days?: number;
  /** Narrows the request list only, never the breakdowns. */
  model?: string;
  keyId?: string;
}

/**
 * What this organization has spent on inference.
 *
 * Polls on the same 60s cadence as the balance. A customer who has just made a
 * call wants to see it land, and the alternative — refetch on focus only — puts
 * a stale zero in front of somebody who is testing an integration.
 *
 * `placeholderData` keeps the previous window on screen while a new range loads,
 * so changing the picker redraws the chart rather than replacing the whole page
 * with a spinner. The numbers are briefly the old range's, which is why the view
 * dims itself while `isFetching` — a stale figure that looks live is the one
 * outcome worth avoiding on a page about money.
 */
export function useInferenceUsage(
  orgId: string | undefined,
  query: InferenceUsageQuery = {},
): UseQueryResult<InferenceUsage> {
  const search = new URLSearchParams();
  if (query.days !== undefined) search.set("days", String(query.days));
  if (query.model) search.set("model", query.model);
  if (query.keyId) search.set("keyId", query.keyId);
  const suffix = search.size > 0 ? `?${search}` : "";

  return useQuery({
    queryKey: keys.inferenceUsage(orgId ?? "", query),
    queryFn: () => api<InferenceUsage>(`${base(orgId!)}/inference/usage${suffix}`),
    enabled: Boolean(orgId),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
}

/** Receipts for completed top-ups. Admin-gated on the API, like payments. */
/**
 * Receipts, and who issued them.
 *
 * Both, from one request, because a receipt is only half a document — it says
 * what was paid and not who was paid — and assembling one from two responses is
 * two chances to render half of it. `issuer` is null when the deployment has not
 * been given a legal name and address, which the page reads as "no invoice can
 * be issued" rather than printing a blank letterhead.
 */
/**
 * Every document a customer holds for a payment, ours and a seller's alike.
 *
 * Replaces `useReceipts`. The endpoint is `/documents` rather than `/receipts`
 * because the dashboard is a static build deployed on its own schedule — an
 * older one keeps asking for receipts and keeps getting exactly what it did,
 * rather than rendering a merchant of record's invoice as a receipt with no
 * number.
 */
export function useBillingDocuments(
  orgId: string | undefined,
  enabled: boolean,
  limit = 25,
): UseQueryResult<{ documents: BillingDocument[]; issuer: BillingIssuer | null; total: number }> {
  return useQuery({
    queryKey: keys.documents(orgId ?? "", limit),
    queryFn: async () => {
      const body = await api<
        Paginated<BillingDocument> & { issuer: BillingIssuer | null; total: number }
      >(`${base(orgId!)}/billing/documents?limit=${limit}`);
      return { documents: body.data, issuer: body.issuer, total: body.total };
    },
    enabled: Boolean(orgId) && enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Where to read a seller's own invoice for a payment.
 *
 * A mutation rather than a query because it is a request, not a fact: some
 * sellers generate invoices on demand, so the first ask can legitimately answer
 * "not yet" and the next one a moment later succeeds. Caching that null would
 * mean the second click showing the same nothing.
 */
export function useInvoiceUrl(orgId: string) {
  return useMutation({
    mutationFn: (paymentId: string) =>
      api<{ url: string | null }>(`${base(orgId)}/billing/payments/${paymentId}/invoice`),
  });
}

/**
 * Saves the organization's billing identity.
 *
 * Sends only what changed. Every field is nullable and the API distinguishes
 * absent from null — leave alone against clear — so submitting the whole form
 * every time would make a cleared address indistinguishable from an untouched
 * one only by luck of what the form happened to hold.
 */
export interface CloseOrgInput {
  /** The caller's own password, and the organization's name typed out. */
  password: string;
  confirmName: string;
}

export interface CloseOrgResult {
  closesAt: string;
  graceDays: number;
  creditForfeitedMicros: number;
}

/**
 * Schedules the closure of an organization. Owner only.
 *
 * Nothing is deleted by this call — it sets the date and makes the organization
 * read-only until then, so the window is real and cancellable.
 */
export function useCloseOrg(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CloseOrgInput) =>
      api<CloseOrgResult>(`${base(orgId!)}/closure`, { method: "POST", body: input }),
    onSuccess: () => {
      // Everything reads differently once the organization is read-only, so the
      // whole cache rather than one key.
      void queryClient.invalidateQueries();
    },
  });
}

/** Calls off a scheduled closure. */
export function useCancelOrgClosure(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<Organization>(`${base(orgId!)}/closure`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useUpdateOrg(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<OrganizationInput>) =>
      api<OrganizationDetail>(base(orgId!), { method: "PATCH", body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.org(orgId ?? "") });
      /*
       * Only the org query. The switcher's organization list lives in
       * `AuthProvider` state rather than in this cache, and this form changes
       * the billing identity rather than the display name — so there is nothing
       * in the nav that has gone stale.
       */
    },
  });
}

// ------------------------------------------------------------------ plans

export function usePlans(
  orgId: string | undefined,
  kind: "app" | "database" = "app",
): UseQueryResult<Plan[]> {
  return useQuery({
    queryKey: keys.plans(orgId ?? "", kind),
    queryFn: async () =>
      (await api<Paginated<Plan>>(`${base(orgId!)}/plans?kind=${kind}`)).data,
    enabled: Boolean(orgId),
    // The catalogue changes only when an operator edits it.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The regions a workload can be created in, with the organization's standing.
 *
 * Fresher than the plan catalogue, which is cached for minutes: availability
 * follows fleet health, and a region shown available from five minutes ago is
 * a create that answers 409. Thirty seconds is short enough for that and long
 * enough that moving between the two create forms, or between a detail
 * header and a form, does not ask the fleet twice.
 */
export function useRegions(orgId: string | undefined): UseQueryResult<RegionCatalogue> {
  return useQuery({
    queryKey: keys.regions(orgId ?? ""),
    queryFn: () => api<RegionCatalogue>(`${base(orgId!)}/regions`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

/**
 * Where this organization's workloads call out from.
 *
 * Cached for a minute rather than thirty seconds: unlike region availability,
 * this only moves when a server is drained or an operator states an address,
 * and it is read on a screen somebody is typing a connection string into.
 */
export function useEgress(orgId: string | undefined): UseQueryResult<EgressCatalogue> {
  return useQuery({
    queryKey: keys.egress(orgId ?? ""),
    queryFn: () => api<EgressCatalogue>(`${base(orgId!)}/egress`),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------- runtime

/**
 * Polled while something is wrong and idle otherwise.
 *
 * A container that is restarting or unhealthy is changing, and someone is
 * watching it; a healthy one is not worth two calls to the deployment engine
 * every few seconds.
 */
export function useAppRuntime(
  orgId: string | undefined,
  appId: string | undefined,
): UseQueryResult<RuntimeReport> {
  return useQuery({
    queryKey: keys.appRuntime(orgId ?? "", appId ?? ""),
    queryFn: () => api<RuntimeReport>(`${appBase(orgId!, appId!)}/runtime`),
    enabled: Boolean(orgId && appId),
    refetchInterval: runtimeInterval,
    // Someone returning to the tab is the moment the reading has to be current,
    // and it is also the moment the interval has been idle longest. Overrides
    // the app-wide default, which is off.
    refetchOnWindowFocus: true,
  });
}

/**
 * How often to re-read a workload's runtime.
 *
 * Polling faster than the collector samples buys nothing: it records one
 * container sample per interval — a minute by default — so a five-second poll
 * returns the same row eleven times out of twelve. Each call is three Dokploy
 * round trips and a Postgres connection with no pooler in front of it, so the
 * cost of over-polling is real while the benefit is zero.
 *
 * Freshness where it matters comes from refetching on focus instead, and from
 * the panel stating the sample's age rather than implying it is live.
 */
function runtimeInterval(query: {
  state: { data?: RuntimeReport };
}): number | false {
  const state = query.state.data?.container?.state;
  const health = query.state.data?.container?.health;
  // A container that is restarting or starting up is changing by the second,
  // and whoever is looking at it is waiting on exactly that. This is the one
  // case where the container's own state, not the sampled metrics, is the
  // thing being watched — so it is worth the requests.
  if (state === "restarting" || state === "created" || health === "starting") return 5_000;
  if (state === "running") return 60_000;
  // Stopped, exited, absent: nothing is producing samples to fetch.
  return false;
}

export function useRestartApp(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ restarted: boolean }>(`${appBase(orgId, appId)}/restart`, { method: "POST" }),
    onSuccess: () => {
      track("app_lifecycle", { object: "app", id: appId, detail: "restart" });
      void queryClient.invalidateQueries({ queryKey: keys.appRuntime(orgId, appId) });
    },
  });
}

export function useDatabaseRuntime(
  orgId: string | undefined,
  databaseId: string | null,
): UseQueryResult<RuntimeReport> {
  return useQuery({
    queryKey: keys.databaseRuntime(orgId ?? "", databaseId ?? ""),
    queryFn: () => api<RuntimeReport>(`${base(orgId!)}/databases/${databaseId}/runtime`),
    enabled: Boolean(orgId && databaseId),
    // Same cadence and the same reasoning as the app runtime.
    refetchInterval: runtimeInterval,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------- backups

export function useBackupSchedule(
  orgId: string | undefined,
  databaseId: string | null,
): UseQueryResult<{ data: BackupSchedule | null; supported: boolean }> {
  return useQuery({
    queryKey: keys.backupSchedule(orgId ?? "", databaseId ?? ""),
    queryFn: () =>
      api<{ data: BackupSchedule | null; supported: boolean }>(
        `${base(orgId!)}/databases/${databaseId}/backups`,
      ),
    enabled: Boolean(orgId && databaseId),
  });
}

/**
 * The one knob a customer has over a database's backups.
 *
 * Storage, frequency and retention are the platform's, chosen per server — the
 * toggle only turns the inherited schedule on or off. It also changes the
 * burn rate (backups cost 30% of the plan), so billing is refetched with it.
 */
export function useSetBackupEnabled(orgId: string, databaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { enabled: boolean; databaseName?: string }) =>
      api<BackupSchedule>(`${base(orgId)}/databases/${databaseId}/backups`, {
        method: "PUT",
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.backupSchedule(orgId, databaseId) });
      // The toggle changes what this database costs per hour.
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.database(orgId, databaseId) });
    },
  });
}

export function useRunBackup(orgId: string, databaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ started: boolean }>(`${base(orgId)}/databases/${databaseId}/backups/run`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.backupSchedule(orgId, databaseId) });
      void queryClient.invalidateQueries({ queryKey: keys.backupFiles(orgId, databaseId) });
    },
  });
}

export function useBackupFiles(
  orgId: string | undefined,
  databaseId: string | null,
  enabled: boolean,
): UseQueryResult<{ data: BackupFile[]; unavailable?: boolean }> {
  return useQuery({
    queryKey: keys.backupFiles(orgId ?? "", databaseId ?? ""),
    queryFn: () =>
      api<{ data: BackupFile[]; unavailable?: boolean }>(
        `${base(orgId!)}/databases/${databaseId}/backups/files`,
      ),
    enabled: Boolean(orgId && databaseId) && enabled,
  });
}

/**
 * Restores this database was the source or target of, newest first.
 *
 * Polled while one is running — a restore takes minutes and its log arrives in
 * batches — and idle otherwise.
 */
export function useRestores(
  orgId: string | undefined,
  databaseId: string | null,
): UseQueryResult<{ data: DatabaseRestore[] }> {
  return useQuery({
    queryKey: keys.restores(orgId ?? "", databaseId ?? ""),
    queryFn: () =>
      api<{ data: DatabaseRestore[] }>(`${base(orgId!)}/databases/${databaseId}/backups/restores`),
    enabled: Boolean(orgId && databaseId),
    refetchInterval: (query) =>
      query.state.data?.data.some((r) => r.status === "saving" || r.status === "restoring") ? 3_000 : false,
  });
}

/**
 * Starts a restore of one of this database's dumps — into itself, or into
 * `targetDatabaseId`. `confirm` is the target's name as typed.
 */
export function useStartRestore(orgId: string, databaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; targetDatabaseId?: string; confirm: string }) =>
      api<DatabaseRestore>(`${base(orgId)}/databases/${databaseId}/backups/restore`, {
        method: "POST",
        body: input,
      }),
    onSuccess: (restore) => {
      void queryClient.invalidateQueries({ queryKey: keys.restores(orgId, databaseId) });
      if (restore.targetDatabaseId !== databaseId) {
        void queryClient.invalidateQueries({ queryKey: keys.restores(orgId, restore.targetDatabaseId) });
      }
    },
  });
}

/**
 * Asks for a link to one dump.
 *
 * A mutation rather than a query because it is an action with an audit entry
 * behind it, and because the link it returns expires — caching one would hand
 * somebody a dead URL on the second click. Nothing is cached, so every download
 * asks again.
 */
export function useBackupDownload(orgId: string, databaseId: string) {
  return useMutation({
    mutationFn: (key: string) =>
      api<{ url: string; expiresAt: string }>(
        `${base(orgId)}/databases/${databaseId}/backups/files/download?key=${encodeURIComponent(key)}`,
      ),
  });
}

// -------------------------------------------------------------- databases

export function useDatabases(
  orgId: string | undefined,
  limit = 25,
): UseQueryResult<{ data: Database[]; total: number }> {
  return useQuery({
    queryKey: keys.databases(orgId ?? "", limit),
    queryFn: () =>
      api<{ data: Database[]; total: number }>(`${base(orgId!)}/databases?limit=${limit}`),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
    // Poll while anything is still coming up, then fall idle.
    refetchInterval: (query) =>
      query.state.data?.data.some((d) => d.state === "creating") ? 5_000 : false,
  });
}

export function useDatabase(
  orgId: string | undefined,
  databaseId: string | undefined,
): UseQueryResult<Database> {
  return useQuery({
    queryKey: keys.database(orgId ?? "", databaseId ?? ""),
    queryFn: () => api<Database>(`${base(orgId!)}/databases/${databaseId}`),
    enabled: Boolean(orgId && databaseId),
    refetchInterval: (query) => (query.state.data?.status.value === "creating" ? 5_000 : false),
  });
}

/**
 * Fetched on demand rather than with the list: it contains a password, so it
 * needs a higher role and should not be sitting in memory for every viewer.
 */
export function useDatabaseConnection(
  orgId: string | undefined,
  databaseId: string | null,
): UseQueryResult<DatabaseConnection> {
  return useQuery({
    queryKey: keys.databaseConnection(orgId ?? "", databaseId ?? ""),
    queryFn: () => api<DatabaseConnection>(`${base(orgId!)}/databases/${databaseId}/connection`),
    enabled: Boolean(orgId && databaseId),
  });
}

export function useCreateDatabase(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      engine: DatabaseEngine;
      planId: string;
      databaseName?: string;
      /** A region code, or null for automatic. See `CreateAppPayload.region`. */
      region?: string | null;
    }) => api<CreatedDatabase>(`${base(orgId)}/databases`, { method: "POST", body: payload, idempotent: true }),
    onSuccess: (created, payload) => {
      track("database_create", { object: "database", id: created.id, detail: payload.engine });
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

export function useDatabaseLifecycle(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "start" | "stop" }) =>
      api<{ id: string; state: string }>(`${base(orgId)}/databases/${id}/${action}`, {
        method: "POST",
      }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.database(orgId, id) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

export function useAttachDatabase(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      databaseId,
      ...body
    }: {
      databaseId: string;
      appId: string;
      key: string;
      redeploy: boolean;
    }) =>
      api<{ attached: boolean; key: string; redeployed: boolean }>(
        `${base(orgId)}/databases/${databaseId}/attach`,
        { method: "POST", body },
      ),
    onSuccess: (_, { databaseId }) => {
      // The moment an app and a database become one deployment rather than two
      // resources somebody is paying for separately.
      track("database_attach", { object: "database", id: databaseId });
      void queryClient.invalidateQueries({ queryKey: keys.apps(orgId) });
    },
  });
}

export function useDatabaseLogs(
  orgId: string | undefined,
  databaseId: string | null,
  options: { tail: number; since: string; search?: string },
): UseQueryResult<{ logs: string; unavailable?: boolean }> {
  return useQuery({
    queryKey: keys.databaseLogs(orgId ?? "", databaseId ?? "", options),
    queryFn: () =>
      api<{ logs: string; unavailable?: boolean }>(
        `${base(orgId!)}/databases/${databaseId}/logs?tail=${options.tail}&since=${options.since}` +
          (options.search ? `&search=${encodeURIComponent(options.search)}` : ""),
      ),
    enabled: Boolean(orgId && databaseId),
  });
}

/**
 * Runtime logs for an application.
 *
 * No tail, since or search: `application.readLogs` upstream takes none, unlike
 * the per-engine database endpoints, so the whole log comes back and the viewer
 * narrows it. Polls while the caller says the app is running, because a log is
 * only worth re-reading while something is writing to it.
 */
export function useAppLogs(
  orgId: string | undefined,
  appId: string | undefined,
  options: { live: boolean },
): UseQueryResult<{ logs: string; unavailable?: boolean }> {
  return useQuery({
    queryKey: keys.appLogs(orgId ?? "", appId ?? ""),
    queryFn: () =>
      api<{ logs: string; unavailable?: boolean }>(`${base(orgId!)}/apps/${appId}/logs`),
    enabled: Boolean(orgId && appId),
    refetchInterval: options.live ? 10_000 : false,
  });
}

export function useSetExternalAccess(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      databaseId,
      ...body
    }: {
      databaseId: string;
      /*
       * Whether to be reachable, not on what. A host port belongs to the
       * machine rather than to this organization, so the platform allocates it
       * — two tenants sharing a server used to be able to send the same number.
       */
      enabled: boolean;
      confirm?: "expose";
    }) =>
      api<Database>(`${base(orgId)}/databases/${databaseId}/external-access`, {
        method: "PUT",
        body,
      }),
    onSuccess: (_, { databaseId, enabled }) => {
      /*
       * Publishing a database to the internet is measured because the security
       * page says how many are, and a number stated on a public page should
       * come from somewhere that notices it moving.
       */
      track("database_external_access", {
        object: "database",
        id: databaseId,
        detail: enabled ? "opened" : "closed",
      });
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
    },
  });
}

export function useAccessRules(orgId: string | undefined, databaseId: string | undefined) {
  return useQuery({
    queryKey: keys.accessRules(orgId ?? "", databaseId ?? ""),
    queryFn: () =>
      api<AccessRulesResponse>(`${base(orgId!)}/databases/${databaseId}/access-rules`),
    enabled: Boolean(orgId && databaseId),
    /*
     * Enforcement is a claim about another machine, so it is re-read while the
     * panel is open: a rule added seconds ago moves pending -> applied within
     * an agent poll, and the panel should say so without a reload.
     */
    refetchInterval: 30_000,
  });
}

export function useAddAccessRule(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, ...body }: { databaseId: string; cidr: string; label?: string }) =>
      api<{ data: AccessRule[] }>(`${base(orgId)}/databases/${databaseId}/access-rules`, {
        method: "POST",
        body,
      }),
    onSuccess: (_, { databaseId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.accessRules(orgId, databaseId) });
    },
  });
}

export function useRemoveAccessRule(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, ruleId }: { databaseId: string; ruleId: string }) =>
      api<{ ok: true }>(`${base(orgId)}/databases/${databaseId}/access-rules/${ruleId}`, {
        method: "DELETE",
      }),
    onSuccess: (_, { databaseId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.accessRules(orgId, databaseId) });
    },
  });
}

export function useChangeDatabasePlan(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ databaseId, planId }: { databaseId: string; planId: string }) =>
      /*
       * `planNotice` is the API saying what the change actually did — a rate
       * from the next billed hour, not resources applied now. Shown verbatim
       * so the promise on screen is the one the server made.
       */
      api<Database & { planNotice: string }>(`${base(orgId)}/databases/${databaseId}`, {
        method: "PATCH",
        body: { planId },
      }),
    onSuccess: (_, { databaseId }) => {
      track("database_plan_change", { object: "database", id: databaseId });
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
      // The tier is a price, so the billing page's burn rate just moved too.
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

/**
 * Turns high availability on.
 *
 * The request returns when the copy is accepted, not when it finishes — that
 * can take hours, and this must not sit on it. A replay of the same attempt
 * must not start a second copy, so the call carries an idempotency key.
 */
export function useEnableDatabaseHa(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (databaseId: string) =>
      api<Database>(`${base(orgId)}/databases/${databaseId}/ha`, {
        method: "POST",
        idempotent: true,
      }),
    onSuccess: (_, databaseId) => {
      track("database_ha_enable", { object: "database", id: databaseId });
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

export function useRotateDatabasePassword(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (databaseId: string) =>
      api<Database & { connectionString: string | null; staleIn: { id: string; name: string }[] }>(
        `${base(orgId)}/databases/${databaseId}/rotate-password`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
    },
  });
}

export function useDeleteDatabase(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (databaseId: string) =>
      api<{ deleted: boolean; stillReferencedBy: { id: string; name: string }[] }>(
        `${base(orgId)}/databases/${databaseId}`,
        { method: "DELETE" },
      ),
    onSuccess: (_, databaseId) => {
      track("database_delete", { object: "database", id: databaseId });
      void queryClient.invalidateQueries({ queryKey: keys.databases(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

// ---------------------------------------------------------- git connections

export function useGitProviders(orgId: string | undefined): UseQueryResult<GitProviderInfo[]> {
  return useQuery({
    queryKey: keys.gitProviders(orgId ?? ""),
    queryFn: async () => (await api<Paginated<GitProviderInfo>>(`${base(orgId!)}/git/providers`)).data,
    enabled: Boolean(orgId),
    // Which hosts a deployment supports changes only when it is redeployed.
    staleTime: 5 * 60 * 1000,
  });
}

export function useGitConnections(orgId: string | undefined): UseQueryResult<GitConnection[]> {
  return useQuery({
    queryKey: keys.gitConnections(orgId ?? ""),
    queryFn: async () =>
      (await api<Paginated<GitConnection>>(`${base(orgId!)}/git/connections`)).data,
    enabled: Boolean(orgId),
  });
}

/**
 * Hands back the URL to send the browser to. The dashboard navigates there
 * itself rather than following a redirect, because a cross-origin redirect
 * cannot be followed from `fetch` into a page.
 */
/** The GitHub / GitLab accounts the person signs in with -- migration 0146. Empty for a password-only account. */
export function useSigninIdentities() {
  return useQuery({
    queryKey: ["oauth-identities"],
    queryFn: () => api<{ data: { provider: GitProviderId; login: string; linkedAt: string; lastUsedAt: string }[] }>("/v1/auth/oauth/identities"),
    select: (body) => body.data,
    staleTime: 300_000,
  });
}

export function useStartGitAuth(orgId: string) {
  return useMutation({
    onSuccess: (_, { provider }) => {
      /*
       * The start of the handover, not its end: the visitor leaves for the
       * provider's consent screen from here and comes back on a fresh document,
       * so a completion event would have to be pushed by whatever route they
       * land on. Started-and-never-connected is the interesting number anyway.
       */
      track("git_connect_start", { object: "git", detail: provider });
    },
    mutationFn: ({ provider, redirectTo }: { provider: GitProviderId; redirectTo?: string }) =>
      api<{ authorizeUrl: string }>(`${base(orgId)}/git/connections/${provider}/authorize`, {
        method: "POST",
        body: { ...(redirectTo ? { redirectTo } : {}) },
      }),
  });
}

export function useDisconnectGit(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      api<void>(`${base(orgId)}/git/connections/${connectionId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.gitConnections(orgId) });
    },
  });
}

export function useGitRepos(
  orgId: string | undefined,
  connectionId: string | null,
  page: number,
): UseQueryResult<{ data: GitRepo[]; page: number; hasMore: boolean }> {
  return useQuery({
    queryKey: keys.gitRepos(orgId ?? "", connectionId ?? "", page),
    queryFn: () =>
      api<{ data: GitRepo[]; page: number; hasMore: boolean }>(
        `${base(orgId!)}/git/connections/${connectionId}/repos?page=${page}&perPage=50`,
      ),
    enabled: Boolean(orgId && connectionId),
    // Repository lists are slow to fetch and rarely change mid-session.
    staleTime: 60 * 1000,
  });
}

export function useGitBranches(
  orgId: string | undefined,
  connectionId: string | null,
  repo: { owner: string; repository: string } | null,
): UseQueryResult<GitBranch[]> {
  return useQuery({
    queryKey: keys.gitBranches(orgId ?? "", connectionId ?? "", repo ? `${repo.owner}/${repo.repository}` : ""),
    queryFn: async () =>
      (
        await api<Paginated<GitBranch>>(
          `${base(orgId!)}/git/connections/${connectionId}/branches` +
            `?owner=${encodeURIComponent(repo!.owner)}&repository=${encodeURIComponent(repo!.repository)}`,
        )
      ).data,
    enabled: Boolean(orgId && connectionId && repo),
  });
}

/** The app's replica changes, newest first. Polled while the tab is open: the rule acts every five minutes. */
export function useAppScaling(orgId: string | undefined, appId: string | undefined) {
  return useQuery({
    queryKey: [...keys.app(orgId ?? "", appId ?? ""), "scaling"] as const,
    queryFn: () =>
      api<{ replicas: number; autoscale: ScaleRule | null; data: ScaleEvent[] }>(`${appBase(orgId!, appId!)}/scaling`),
    enabled: Boolean(orgId && appId),
    refetchInterval: 60_000,
  });
}

export function useUpdateApp(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<Application>(appBase(orgId, appId), { method: "PATCH", body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.app(orgId, appId) });
      void queryClient.invalidateQueries({ queryKey: keys.apps(orgId) });
    },
  });
}

export function useDeleteApp(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) => api<void>(appBase(orgId, appId), { method: "DELETE" }),
    onSuccess: (_, appId) => {
      track("app_delete", { object: "app", id: appId });
      void queryClient.invalidateQueries({ queryKey: keys.apps(orgId) });
    },
  });
}

export type LifecycleAction = "deploy" | "redeploy" | "stop" | "start";

export function useAppLifecycle(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /*
     * `title` and `cleanCache` are the body of a deploy or a redeploy; a stop or
     * a start takes none. `cleanCache` holds for the one build this request
     * starts — the platform turns it back off, so nothing here has to.
     */
    mutationFn: ({ action, ...body }: { action: LifecycleAction; title?: string; cleanCache?: boolean }) =>
      api<{ id: string; state: string; rebuilding?: boolean }>(`${appBase(orgId, appId)}/${action}`, {
        method: "POST",
        body: action === "deploy" || action === "redeploy" ? body : undefined,
      }),
    onSuccess: (_, { action }) => {
      /*
       * Deploying has its own name because it is the product's central act and
       * a report that has to filter a generic lifecycle event to find it will
       * not be built. Starting and stopping share one, since what matters there
       * is that the pair happened rather than which half.
       */
      const deployed = action === "deploy" || action === "redeploy";
      track(deployed ? "app_deploy" : "app_lifecycle", {
        object: "app",
        id: appId,
        detail: action,
      });
      void queryClient.invalidateQueries({ queryKey: keys.app(orgId, appId) });
      void queryClient.invalidateQueries({ queryKey: keys.apps(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.deployments(orgId, appId) });
    },
  });
}

// ----------------------------------------------------------- deployments

/**
 * A page of deployment history, newest first, with how many exist in total.
 *
 * `limit` is part of the key, so raising it is a fresh fetch rather than a
 * client-side slice of something already too big — the route used to return
 * every deployment an app had ever had.
 */
export function useDeployments(
  orgId: string | undefined,
  appId: string | undefined,
  limit = 50,
): UseQueryResult<{ data: Deployment[]; total: number }> {
  return useQuery({
    queryKey: keys.deployments(orgId ?? "", appId ?? "", limit),
    queryFn: () =>
      api<{ data: Deployment[]; total: number }>(
        `${appBase(orgId!, appId!)}/deployments?limit=${limit}`,
      ),
    enabled: Boolean(orgId && appId),
    /*
     * Keep the page already on screen while a larger one loads, so "Show
     * older" extends the list rather than blanking it and rebuilding. The
     * caller reads `isPlaceholderData` for the pending state — `isFetching`
     * would also be true on every poll of a running build, which would blink
     * the control for a fetch nobody asked for.
     */
    placeholderData: keepPreviousData,
    // Poll while a build is in flight, then fall idle.
    refetchInterval: (query) =>
      query.state.data?.data.some((d) => d.status === "running") ? 5_000 : false,
  });
}

export function useDeploymentLogs(
  orgId: string | undefined,
  appId: string | undefined,
  deploymentId: string | null,
): UseQueryResult<DeploymentLogs> {
  return useQuery({
    queryKey: keys.logs(orgId ?? "", appId ?? "", deploymentId ?? ""),
    queryFn: () =>
      api<DeploymentLogs>(`${appBase(orgId!, appId!)}/deployments/${deploymentId}/logs`),
    enabled: Boolean(orgId && appId && deploymentId),
    refetchInterval: (query) => (query.state.data?.complete === false ? 3_000 : false),
  });
}

export function useCancelDeployment(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) =>
      api<void>(`${appBase(orgId, appId)}/deployments/${deploymentId}/cancel`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.deployments(orgId, appId) });
    },
  });
}

// ------------------------------------------------------- env and domains

export function useEnvVars(
  orgId: string | undefined,
  appId: string | undefined,
): UseQueryResult<EnvVar[]> {
  return useQuery({
    queryKey: keys.env(orgId ?? "", appId ?? ""),
    queryFn: async () => (await api<Paginated<EnvVar>>(`${appBase(orgId!, appId!)}/env`)).data,
    enabled: Boolean(orgId && appId),
  });
}

export function useReplaceEnv(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      env: { key: string; value: string | null; isSecret: boolean }[];
      redeploy: boolean;
    }) => api<Paginated<EnvVar>>(`${appBase(orgId, appId)}/env`, { method: "PUT", body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.env(orgId, appId) });
      void queryClient.invalidateQueries({ queryKey: keys.deployments(orgId, appId) });
    },
  });
}

export function useDomains(
  orgId: string | undefined,
  appId: string | undefined,
  poll = false,
): UseQueryResult<Domain[]> {
  return useQuery({
    queryKey: keys.domains(orgId ?? "", appId ?? ""),
    queryFn: async () => (await api<Paginated<Domain>>(`${appBase(orgId!, appId!)}/domains`)).data,
    enabled: Boolean(orgId && appId),
    // The Domains tab polls while a domain is pending so that fixing DNS at
    // the registrar flips the row to Active without a click.
    refetchInterval: poll ? 15_000 : false,
  });
}

export function useCreateDomain(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      host: string;
      path: string;
      /** Omitted means "whatever the app listens on", resolved server-side. */
      port?: number;
      https: boolean;
      certificateType: "letsencrypt" | "none" | "custom";
    }) => api<Domain>(`${appBase(orgId, appId)}/domains`, { method: "POST", body: payload }),
    onSuccess: (created, payload) => {
      // The certificate choice, not the hostname: the host is the customer's
      // own brand and belongs in their account rather than in an analytics
      // property.
      track("domain_add", { object: "domain", id: created.id, detail: payload.certificateType });
      void queryClient.invalidateQueries({ queryKey: keys.domains(orgId, appId) });
    },
  });
}

export function useDeleteDomain(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) =>
      api<void>(`${appBase(orgId, appId)}/domains/${domainId}`, { method: "DELETE" }),
    onSuccess: (_, domainId) => {
      track("domain_remove", { object: "domain", id: domainId });
      void queryClient.invalidateQueries({ queryKey: keys.domains(orgId, appId) });
    },
  });
}

export function useRecheckDomain(orgId: string, appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) =>
      api<Domain>(`${appBase(orgId, appId)}/domains/${domainId}/recheck`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.domains(orgId, appId) });
    },
  });
}

// --------------------------------------------------------- team and keys

export function useMembers(orgId: string | undefined): UseQueryResult<Member[]> {
  return useQuery({
    queryKey: keys.members(orgId ?? ""),
    queryFn: async () => (await api<Paginated<Member>>(`${base(orgId!)}/members`)).data,
    enabled: Boolean(orgId),
  });
}

export function useInvitations(
  orgId: string | undefined,
  enabled: boolean,
): UseQueryResult<Invitation[]> {
  return useQuery({
    queryKey: keys.invitations(orgId ?? ""),
    queryFn: async () =>
      (await api<Paginated<Invitation>>(`${base(orgId!)}/members/invitations`)).data,
    enabled: Boolean(orgId) && enabled,
  });
}

export function useInviteMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; role: MemberRole }) =>
      api<CreatedInvitation>(`${base(orgId)}/members/invitations`, {
        method: "POST",
        body: payload, idempotent: true
      }),
    onSuccess: (created, payload) => {
      /*
       * The role, never the address. `track` drops anything containing an "@"
       * as well, so this is guarded twice — the payload here is the single most
       * likely place in the dashboard for an email to reach Google by accident.
       */
      track("member_invite", { object: "member", id: created.id, detail: payload.role });
      void queryClient.invalidateQueries({ queryKey: keys.invitations(orgId) });
    },
  });
}

export function useRevokeInvitation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<void>(`${base(orgId)}/members/invitations/${invitationId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.invitations(orgId) });
    },
  });
}

export function useResendInvitation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<CreatedInvitation>(`${base(orgId)}/members/invitations/${invitationId}/resend`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.invitations(orgId) });
    },
  });
}

export function useUpdateMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      api<{ userId: string; role: MemberRole }>(`${base(orgId)}/members/${userId}`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members(orgId) });
    },
  });
}

export function useRemoveMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<void>(`${base(orgId)}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members(orgId) });
    },
  });
}

/**
 * The signed-in user leaves the organization. The caller reloads the session
 * afterwards — the departed org must disappear from the switcher immediately.
 */
export function useLeaveOrg(orgId: string) {
  return useMutation({
    mutationFn: () => api<void>(`${base(orgId)}/members/me`, { method: "DELETE" }),
  });
}

export type ApiKeyState = "active" | "revoked";

/**
 * One page of the organization's keys in one state, newest first.
 *
 * Paged because revoked keys are kept for good, so the list only grows. Live
 * and revoked page separately — they are separate cards, and a page of one
 * should not shift because the other grew. `keepPreviousData` so "Show more"
 * extends the list in place rather than blanking it.
 */
/** The organization's ceilings, usage and requests -- the Limits page. */
export function useOrgQuota(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.quota(orgId ?? ""),
    queryFn: () => api<{ data: OrgQuota }>(`${base(orgId!)}/quota`).then((r) => r.data),
    enabled: Boolean(orgId),
  });
}

export function useRequestQuota(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { resource: QuotaResource; requestedLimit: number; reason: string }) =>
      api<{ data: QuotaRequest }>(`${base(orgId)}/quota/requests`, { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.quota(orgId) });
    },
  });
}

export function useApiKeys(
  orgId: string | undefined,
  enabled: boolean,
  options: { state: ApiKeyState; limit?: number },
): UseQueryResult<{ data: ApiKey[]; total: number }> {
  const limit = options.limit ?? 25;
  return useQuery({
    queryKey: keys.apiKeyPage(orgId ?? "", options.state, limit),
    queryFn: () =>
      api<{ data: ApiKey[]; total: number }>(
        `${base(orgId!)}/api-keys?state=${options.state}&limit=${limit}`,
      ),
    enabled: Boolean(orgId) && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCreateApiKey(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; role: MemberRole; expiresInDays?: number } & ApiKeyLimits) =>
      api<CreatedApiKey>(`${base(orgId)}/api-keys`, { method: "POST", body: payload, idempotent: true }),
    onSuccess: (created, payload) => {
      // Issuing a key is a customer moving from the dashboard to the API, which
      // is the one adoption step no pageview can show.
      track("api_key_create", { object: "api_key", id: created.id, detail: payload.role });
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys(orgId) });
    },
  });
}

/**
 * Name, monthly inference limit and model allowlist change in place; the
 * secret and the role do not. The event's `detail` names which fields moved
 * and never their values — an allowlist is a customer's policy, not a metric.
 */
export function useUpdateApiKey(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, ...patch }: { keyId: string; name?: string } & ApiKeyLimits) =>
      api<ApiKey>(`${base(orgId)}/api-keys/${keyId}`, { method: "PATCH", body: patch }),
    onSuccess: (updated, { keyId, ...patch }) => {
      const changed = [
        ...(patch.name !== undefined ? ["name"] : []),
        ...(patch.inferenceMonthlyLimitMicros !== undefined ? ["limit"] : []),
        ...(patch.allowedModels !== undefined ? ["models"] : []),
      ].join(",");
      track("api_key_update", { object: "api_key", id: keyId, detail: changed });
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys(orgId) });
      void updated;
    },
  });
}
/**
 * A new secret for the same key; the old one stops at once. Not idempotent, on
 * purpose: a retry rotates again, which costs nothing when the previous secret
 * is already dead, and the API keeps the token out of its stored responses.
 */
export function useRotateApiKey(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      api<CreatedApiKey>(`${base(orgId)}/api-keys/${keyId}/rotate`, { method: "POST" }),
    onSuccess: (_, keyId) => {
      track("api_key_rotate", { object: "api_key", id: keyId });
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys(orgId) });
    },
  });
}

export function useRevokeApiKey(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api<void>(`${base(orgId)}/api-keys/${keyId}`, { method: "DELETE" }),
    onSuccess: (_, keyId) => {
      track("api_key_revoke", { object: "api_key", id: keyId });
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys(orgId) });
    },
  });
}

// --------------------------------------------------------- announcements

/**
 * Live operator notices, for whoever is asking.
 *
 * Two endpoints behind one hook, because the caller — a layout — should not have
 * to know which. With an organization it asks the scoped route, which adds
 * notices aimed at a server this tenant has something on; without one it asks
 * the public route, which is what the login page needs and is the surface that
 * matters most when the platform is the thing that is broken.
 *
 * `anonymous` on the public call is not a detail. Without it a signed-out
 * visitor's 401-free request still drags the refresh-and-replay machinery along,
 * and a failed refresh drops the page to `/login` — which is where they already
 * are, in a loop.
 *
 * Polled, and this is the one place in the app where an unconditional interval
 * is right: the point of the banner is to appear without the customer reloading
 * anything, and a dashboard left open on a second monitor is exactly where an
 * incident notice needs to arrive. Five minutes, against a cached endpoint.
 */
export function useAnnouncements(orgId: string | undefined): UseQueryResult<Announcement[]> {
  return useQuery({
    queryKey: keys.announcements(orgId),
    queryFn: async () => {
      const path = orgId ? `${base(orgId)}/announcements` : "/v1/announcements?surface=dashboard";
      /* Signed out, this is the wide-open endpoint the marketing site reads, and
         it answers with a wildcard origin. A cookie sent to it is not merely
         unnecessary -- the browser refuses the response, so the banner silently
         never appears on the signed-out screens. */
      const response = await api<{ data: Announcement[] }>(
        path,
        orgId ? {} : { anonymous: true, omitCredentials: true },
      );
      return response.data;
    },
    refetchInterval: 300_000,
    /*
     * A banner is furniture, not a feature: if the endpoint is down there is
     * nothing to show and nothing to say about it. Retrying once keeps a
     * transient failure from silencing a live incident notice for five minutes,
     * and stopping there keeps a real outage from turning every dashboard into a
     * retry loop against the API that is already struggling.
     */
    retry: 1,
  });
}

// --------------------------------------------------------------- support

/**
 * The organization's tickets, not the signed-in person's.
 *
 * A ticket belongs to the organization on purpose — a colleague picks up a
 * thread while its author is away, which is the whole difference between this
 * and the email thread it replaces.
 */
export interface TicketQuery {
  status?: SupportStatus;
  category?: SupportCategory;
  /** Matches the subject and the reference, server-side. See below. */
  q?: string;
  limit?: number;
}

export function useTickets(
  orgId: string | undefined,
  query: TicketQuery = {},
): UseQueryResult<{ data: SupportTicket[]; total: number }> {
  const search = new URLSearchParams({ limit: String(query.limit ?? 25) });
  if (query.status) search.set("status", query.status);
  if (query.category) search.set("category", query.category);
  /*
   * Searching and filtering happen on the server rather than over the loaded
   * rows, because the list is paged: a search that only looked at the first
   * twenty-five would fail to find exactly the old ticket somebody is
   * searching for, and would do it silently.
   */
  if (query.q) search.set("q", query.q);

  return useQuery({
    queryKey: keys.tickets(orgId ?? "", query),
    queryFn: () =>
      api<{ data: SupportTicket[]; total: number }>(`${base(orgId!)}/tickets?${search}`),
    enabled: Boolean(orgId),
    /* Growing the limit re-fetches; keeping the previous page on screen makes
       "Show more" extend the list rather than blank it. */
    placeholderData: keepPreviousData,
  });
}

/**
 * How many tickets sit in each state, whatever the list is currently showing.
 *
 * Its own request so the tabs describe the account rather than the page: a
 * "Resolved 4" on an organization with forty resolved tickets is a wrong answer
 * presented as a right one.
 */
export function useTicketCounts(
  orgId: string | undefined,
): UseQueryResult<{ open: number; pending: number; resolved: number; closed: number; total: number }> {
  return useQuery({
    queryKey: keys.ticketCounts(orgId ?? ""),
    queryFn: () =>
      api<{ open: number; pending: number; resolved: number; closed: number; total: number }>(
        `${base(orgId!)}/tickets/counts`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useTicket(
  orgId: string | undefined,
  ticketId: string | undefined,
): UseQueryResult<SupportTicketDetail> {
  return useQuery({
    queryKey: keys.ticket(orgId ?? "", ticketId ?? ""),
    queryFn: () => api<SupportTicketDetail>(`${base(orgId!)}/tickets/${ticketId}`),
    enabled: Boolean(orgId && ticketId),
    /*
     * Polled while the ticket is waiting on us. An answer arrives from a person
     * rather than from a job, so there is no state to derive it from — and
     * somebody sitting on the page during an incident should not have to
     * reload to find out we replied. Falls idle the moment the ball is back in
     * their court.
     */
    refetchInterval: (query) => (query.state.data?.status === "open" ? 30_000 : false),
  });
}

export function useCreateTicket(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      subject: string;
      category: SupportCategory;
      priority: SupportPriority;
      message: string;
      appId?: string | null;
      databaseId?: string | null;
    }) =>
      api<SupportTicket & { firstMessageId: string }>(`${base(orgId)}/tickets`, {
        method: "POST",
        body: payload, idempotent: true
      }),
    /*
     * Deliberately not tracked, unlike every other mutation in this file.
     *
     * `TrackedFields.object` is a closed vocabulary whose contents are promised
     * in the privacy policy and in Annex A of the DPA, so adding a value is a
     * documentation change before it is a code change — and what it would buy
     * is a count of support tickets, which the operator console already holds
     * exactly rather than approximately. Opening a ticket is also not a step in
     * any funnel: it is what somebody does when a funnel failed.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", orgId, "tickets"] });
    },
  });
}

export function useReplyToTicket(orgId: string, ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<SupportMessage>(`${base(orgId)}/tickets/${ticketId}/messages`, {
        method: "POST",
        body: { body },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", orgId, "tickets"] });
    },
  });
}

/**
 * Attaches one file to a message that has already been posted.
 *
 * Two requests rather than one, matching the API: a reply that lands with a
 * file missing is visible and retryable, while bytes accepted against a message
 * that was rejected is an orphan nobody can see. The composer posts the
 * message, uploads each file, and only then invalidates — so the thread does
 * not render once without its attachments.
 */
export function useUploadAttachment(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /* The ticket is a variable rather than a closure argument, because the
       first upload of a new ticket happens on a page that did not know the id
       when it rendered. */
    mutationFn: ({
      ticketId,
      messageId,
      file,
    }: {
      ticketId: string;
      messageId: string;
      file: File;
    }) =>
      apiUpload<SupportAttachment>(
        `${base(orgId)}/tickets/${ticketId}/messages/${messageId}/attachments`,
        file,
      ),
    onSuccess: (_, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.ticket(orgId, ticketId) });
    },
  });
}

/**
 * Fetches an attachment's bytes with the session's token.
 *
 * Not a hook and not cached: an attachment is fetched when somebody clicks it,
 * and holding a customer's uploads in the query cache would keep every file
 * they have ever opened in memory for the life of the tab.
 */
export function fetchAttachment(orgId: string, ticketId: string, attachmentId: string) {
  return apiBlob(`${base(orgId)}/tickets/${ticketId}/attachments/${attachmentId}`);
}

// ------------------------------------------------------------- schedules

/**
 * Every scheduled job the organization owns, or one app's worth.
 *
 * Reads only the control plane's own database, never the deployment engine, so
 * this page renders during an incident — which is exactly when somebody wants
 * to know what is scheduled to run.
 */
export function useSchedules(
  orgId: string | undefined,
  appId?: string,
): UseQueryResult<ScheduleList> {
  return useQuery({
    queryKey: keys.schedules(orgId ?? "", appId),
    queryFn: () =>
      api<ScheduleList>(`${base(orgId!)}/schedules${appId ? `?appId=${appId}` : ""}`),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
}

/**
 * What the engine recorded about this job's runs, newest first.
 *
 * Polls while a run is in flight and falls idle after, the same as the
 * deployment history it mirrors. A schedule that was never created on the
 * engine answers an empty list rather than an error.
 */
export function useScheduleRuns(
  orgId: string | undefined,
  scheduleId: string | undefined,
  page: { limit: number; offset: number } = { limit: 20, offset: 0 },
): UseQueryResult<ScheduleRunList> {
  return useQuery({
    queryKey: keys.scheduleRuns(orgId ?? "", scheduleId ?? "", page),
    queryFn: () =>
      api<ScheduleRunList>(
        `${base(orgId!)}/schedules/${scheduleId!}/runs?limit=${page.limit}&offset=${page.offset}`,
      ),
    enabled: Boolean(orgId && scheduleId),
    // Keeps the page on screen while the next one loads, so Next moves the
    // list rather than blanking the card and rebuilding it.
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.data.some((run) => run.status === "running") ? 5_000 : false,
  });
}

export function useScheduleRunLogs(
  orgId: string | undefined,
  scheduleId: string | undefined,
  runId: string | null,
): UseQueryResult<ScheduleRunLogs> {
  return useQuery({
    queryKey: keys.scheduleRunLogs(orgId ?? "", scheduleId ?? "", runId ?? ""),
    queryFn: () =>
      api<ScheduleRunLogs>(`${base(orgId!)}/schedules/${scheduleId!}/runs/${runId!}/logs`),
    enabled: Boolean(orgId && scheduleId && runId),
    refetchInterval: (query) => (query.state.data?.complete === false ? 3_000 : false),
  });
}

export function useSchedule(
  orgId: string | undefined,
  scheduleId: string | undefined,
): UseQueryResult<ScheduleDetail> {
  return useQuery({
    queryKey: keys.schedule(orgId ?? "", scheduleId ?? ""),
    queryFn: () => api<ScheduleDetail>(`${base(orgId!)}/schedules/${scheduleId!}`),
    enabled: Boolean(orgId && scheduleId),
  });
}

/**
 * Creating a schedule starts a recurring charge, so billing is refetched with
 * it — the same coupling `useSetBackupEnabled` has, and for the same reason:
 * the burn rate on the billing page is wrong the moment this succeeds.
 */
/**
 * Private connections.
 *
 * This one does reach the deployment engine, unlike the schedules list above:
 * `state` is written at create and by nothing afterwards, so a list served
 * straight from the column reported `running` for a container that had been
 * stopped, indefinitely. The API resolves it per row and writes the answer
 * back.
 *
 * The incident property survives the change rather than being traded for
 * freshness: a server that cannot be reached falls back to the stored value
 * with `stale`, so the page still renders and the badge says that what it is
 * showing is a record.
 */
export function useTunnels(
  orgId: string | undefined,
  page = { limit: 20, offset: 0 },
): UseQueryResult<TunnelList> {
  return useQuery({
    queryKey: [...keys.tunnels(orgId ?? ""), page.limit, page.offset],
    queryFn: () =>
      api<TunnelList>(`${base(orgId!)}/tunnels?limit=${page.limit}&offset=${page.offset}`),
    enabled: Boolean(orgId),
    // Keeps the current page on screen while the next one loads, so paging does
    // not blank the list and jump the page under the reader.
    placeholderData: keepPreviousData,
  });
}

export function useTunnel(
  orgId: string | undefined,
  tunnelId: string | undefined,
): UseQueryResult<TunnelDetail> {
  return useQuery({
    queryKey: keys.tunnel(orgId ?? "", tunnelId ?? ""),
    queryFn: () => api<TunnelDetail>(`${base(orgId!)}/tunnels/${tunnelId}`),
    enabled: Boolean(orgId && tunnelId),
  });
}

/**
 * Whether a connection is carrying traffic.
 *
 * Separate from the detail for the reason `useAppRuntime` is separate: this one
 * costs Dokploy round trips and a Postgres connection with no pooler in front
 * of it, and the detail above is a cheap read that a list renders once per card.
 *
 * Two engine calls, not three: it asks for the container and inspects it, and no
 * longer pulls the CPU and memory series a tunnel renders nowhere. That waste
 * landed exactly here, because an unhealthy container is still a running one and
 * that is the state the poll below runs fastest against.
 *
 * Polled only while something is wrong. A connection that is up is not worth a
 * call every thirty seconds; one that is failing, or has not handshaken yet, is
 * being watched by somebody waiting on exactly that.
 */
export function useTunnelConnectivity(
  orgId: string | undefined,
  tunnelId: string | undefined,
): UseQueryResult<{ connectivity: TunnelConnectivity }> {
  return useQuery({
    queryKey: [...keys.tunnel(orgId ?? "", tunnelId ?? ""), "runtime"],
    queryFn: () =>
      api<{ connectivity: TunnelConnectivity }>(`${base(orgId!)}/tunnels/${tunnelId}/runtime`),
    enabled: Boolean(orgId && tunnelId),
    refetchInterval: (query) => {
      const connectivity = query.state.data?.connectivity;
      if (connectivity === "starting" || connectivity === "unhealthy") return 15_000;
      return false;
    },
    // Returning to the tab is when the reading has to be current, and when the
    // idle interval has been idle longest. Overrides the app-wide default.
    refetchOnWindowFocus: true,
  });
}

export function useCreateTunnel(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; region?: string | null; peer: TunnelPeerInput }) =>
      api<{ data: Tunnel }>(`${base(orgId)}/tunnels`, { method: "POST", body: payload, idempotent: true }),
    onSuccess: (created) => {
      /*
       * The region, which is the choice worth measuring, and nothing else. Not
       * the endpoint and never any part of the key: this event describes that a
       * connection was made, not where to or with what.
       */
      track("tunnel_create", {
        object: "tunnel",
        id: created.data.id,
        detail: created.data.region ?? "auto",
      });
      void queryClient.invalidateQueries({ queryKey: keys.tunnels(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
    },
  });
}

/**
 * Renaming a connection, or replacing the credential behind it.
 *
 * The API has carried this since tunnels shipped and no screen ever called it,
 * which made key rotation — the one maintenance task a WireGuard peer actually
 * has — reachable only by deleting the connection and building a new one. That
 * loses the hostname every app's connection string names and every address on
 * it, so the safe operation was the one nobody could do.
 *
 * Both invalidations, for `invalidateTunnel`'s reason: the list is a sibling
 * key rather than a parent, and a rename that only refreshed the detail would
 * leave the old name in the list behind the back button. Replacing the peer
 * also republishes the container, which can move the state the list renders.
 */
export function useUpdateTunnel(orgId: string, tunnelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name?: string; peer?: TunnelPeerInput }) =>
      api<{ data: Tunnel }>(`${base(orgId)}/tunnels/${tunnelId}`, {
        method: "PATCH",
        body: payload,
      }),
    onSuccess: () => invalidateTunnel(queryClient, orgId, tunnelId),
  });
}

/**
 * Starting and stopping a connection's container.
 *
 * Starting is the half this exists for. Running out of credit stops every
 * workload an organization holds, connections included, and a top-up restores
 * none of them on purpose — so without a control here a customer who paid would
 * have their apps back and no way to bring back the connection those apps
 * reach their own network through.
 *
 * Stopping does not end the charge, which is why the billing keys are not
 * invalidated: billing follows a workload existing, and deleting is what ends
 * it. `useDeleteTunnel` is the one that moves money.
 */
/* ------------------------------------------- free-inference campaign */

export interface FreeInferenceOffer {
  id: string;
  name: string;
  models: string[];
  /** At least this many tokens a month, at the dearest rate on the campaign.
      Null when no model on it publishes a per-token rate. */
  tokensPerKey: number | null;
  /** No keys left, or the pool is spent. Either way nobody new gets in. */
  full: boolean;
  spent: boolean;
  request: { status: "requested" | "approved" | "rejected"; note: string | null } | null;
}

/**
 * Whether there is a free-inference campaign, and where this organization
 * stands with it.
 *
 * `null` data is the ordinary answer — most of the time no campaign is running —
 * so every caller renders absence rather than an error. Not retried: a missing
 * campaign is a fact, not a failure.
 */
export function useFreeInference(orgId: string | undefined) {
  return useQuery({
    queryKey: ["free-inference", orgId],
    queryFn: () => api<{ data: FreeInferenceOffer | null }>(`${base(orgId!)}/inference-campaign`),
    enabled: Boolean(orgId),
    retry: false,
    /*
     * Five minutes, because this answers a question that barely changes and was
     * being asked on every visit to the AI tab.
     *
     * Almost every organization has no application at all — the ask is on the
     * registration form and most signups do not make it — so the default of
     * refetching on every mount was a round trip per navigation to learn `null`
     * again. An operator's verdict arrives by email whatever this says, and the
     * card is where somebody checks afterwards rather than how they find out.
     */
    staleTime: 5 * 60_000,
  });
}

export function useTunnelLifecycle(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "start" | "stop" }) =>
      api<{ id: string; state: string }>(`${base(orgId)}/tunnels/${id}/${action}`, {
        method: "POST",
      }),
    onSuccess: (_result, { id }) => invalidateTunnel(queryClient, orgId, id),
  });
}

export function useDeleteTunnel(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tunnelId: string) =>
      api<{ data: { id: string } }>(`${base(orgId)}/tunnels/${tunnelId}`, { method: "DELETE" }),
    onSuccess: (_result, tunnelId) => {
      /*
       * The id and nothing else, like every other delete here — and the one
       * this set was missing. An app, a database and a schedule each report the
       * event that ends their charge; a tunnel is billed the same way and did
       * not, so the funnel could see connections being made and never see one
       * go. Deleting is the only way to stop a tunnel's meter, which makes it
       * exactly the milestone this event stream is for.
       */
      track("tunnel_delete", { object: "tunnel", id: tunnelId });
      void queryClient.invalidateQueries({ queryKey: keys.tunnels(orgId) });
      // Deleting is what ends the charge, so the billing figures move with it.
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
    },
  });
}

/**
 * Both target writes land the same two invalidations.
 *
 * The detail is obvious. The *list* is the one that was missed: its key is a
 * sibling of the detail's, not a parent, so invalidating `keys.tunnel` leaves
 * every page of `keys.tunnels` untouched — and the list now renders a count of
 * the addresses each tunnel carries. Adding one and going back would show the
 * old number until something else happened to refetch.
 *
 * It is not only the count. Both writes republish the container, which can move
 * the tunnel's state and, on the first publish, give it the hostname the list
 * shows.
 */
function invalidateTunnel(queryClient: QueryClient, orgId: string, tunnelId: string): void {
  void queryClient.invalidateQueries({ queryKey: keys.tunnel(orgId, tunnelId) });
  void queryClient.invalidateQueries({ queryKey: keys.tunnels(orgId) });
}

export function useAddTunnelTarget(orgId: string, tunnelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; remoteHost: string; remotePort: number }) =>
      api<{ data: TunnelTarget[] }>(`${base(orgId)}/tunnels/${tunnelId}/targets`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => invalidateTunnel(queryClient, orgId, tunnelId),
  });
}

export function useRemoveTunnelTarget(orgId: string, tunnelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetId: string) =>
      api<{ data: TunnelTarget[] }>(`${base(orgId)}/tunnels/${tunnelId}/targets/${targetId}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateTunnel(queryClient, orgId, tunnelId),
  });
}

export function useCreateSchedule(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSchedulePayload) =>
      api<Schedule>(`${base(orgId)}/schedules`, { method: "POST", body: payload, idempotent: true }),
    onSuccess: (created) => {
      // Whether they used a preset or wrote their own cron, which is the part
      // of the form worth measuring. Never the command — that is their code.
      track("schedule_create", {
        object: "schedule",
        id: created.id,
        detail: PRESET_CRONS.some((preset) => preset.cron === created.cronExpression)
          ? "preset"
          : "custom",
      });
      void queryClient.invalidateQueries({ queryKey: keys.schedules(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
    },
  });
}

export function useUpdateSchedule(orgId: string, scheduleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateSchedulePayload) =>
      api<Schedule>(`${base(orgId)}/schedules/${scheduleId}`, { method: "PATCH", body: payload }),
    onSuccess: (_updated, payload) => {
      track("schedule_update", {
        object: "schedule",
        id: scheduleId,
        detail:
          payload.enabled === undefined ? "edited" : payload.enabled ? "enabled" : "disabled",
      });
      void queryClient.invalidateQueries({ queryKey: keys.schedules(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.schedule(orgId, scheduleId) });
      // Disabling does not stop the charge, so the burn rate is unchanged by a
      // toggle — but an edit can still be the thing that provisions a failed
      // schedule, and that does change it.
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
    },
  });
}

/** Deleting is the only thing that ends the charge, so billing always moves. */
export function useDeleteSchedule(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      api<DeletedSchedule>(`${base(orgId)}/schedules/${scheduleId}`, { method: "DELETE" }),
    onSuccess: (_result, scheduleId) => {
      track("schedule_delete", { object: "schedule", id: scheduleId });
      void queryClient.invalidateQueries({ queryKey: keys.schedules(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
    },
  });
}

/**
 * Runs the job now.
 *
 * Answers 202, never 200: the engine takes the request and nothing here can
 * say how it went. Everything on screen says "started" rather than
 * "succeeded"; the outcome arrives in the run history a moment later.
 */
export function useRunSchedule(orgId: string, scheduleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ id: string; started: boolean; startedAt: string }>(
        `${base(orgId)}/schedules/${scheduleId}/run`,
        { method: "POST" },
      ),
    onSuccess: () => {
      track("schedule_run", { object: "schedule", id: scheduleId, detail: "manual" });
      void queryClient.invalidateQueries({ queryKey: keys.schedule(orgId, scheduleId) });
      void queryClient.invalidateQueries({ queryKey: keys.schedules(orgId) });
    },
  });
}

// ---------------------------------------------------------------- domains

export function useRegisteredDomains(
  orgId: string | undefined,
  page = { limit: 20, offset: 0 },
): UseQueryResult<RegisteredDomainList> {
  return useQuery({
    queryKey: [...keys.registeredDomains(orgId ?? ""), page.limit, page.offset],
    queryFn: () =>
      api<RegisteredDomainList>(`${base(orgId!)}/domains?limit=${page.limit}&offset=${page.offset}`),
    enabled: Boolean(orgId),
    // Keeps the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  });
}

export function useRegisteredDomain(
  orgId: string | undefined,
  domainId: string | undefined,
): UseQueryResult<RegisteredDomainDetail> {
  return useQuery({
    queryKey: keys.registeredDomain(orgId ?? "", domainId ?? ""),
    queryFn: () => api<RegisteredDomainDetail>(`${base(orgId!)}/domains/${domainId}`),
    enabled: Boolean(orgId && domainId),
    // A registration or transfer the registrar has not confirmed is being
    // watched, and so is a host whose certificate is still being issued;
    // once both have settled, the row changes on its own schedule.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.status === "pending") return 15_000;
      return data.pointing?.hosts.some((host) => host.status === "pending") ? 10_000 : false;
    },
  });
}

/** The extensions on sale and their prices. Not org-scoped: one shelf for everybody. */
export function useDomainTlds(orgId: string | undefined): UseQueryResult<{ data: DomainTld[] }> {
  return useQuery({
    queryKey: ["domain-tlds"] as const,
    queryFn: () => api<{ data: DomainTld[] }>(`${base(orgId!)}/domains/tlds`),
    enabled: Boolean(orgId),
    staleTime: 10 * 60_000,
  });
}

export function useDomainContact(orgId: string | undefined): UseQueryResult<{ data: DomainContact | null }> {
  return useQuery({
    queryKey: keys.domainContact(orgId ?? ""),
    queryFn: () => api<{ data: DomainContact | null }>(`${base(orgId!)}/domains/contact`),
    enabled: Boolean(orgId),
  });
}

export function useSaveDomainContact(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DomainContactInput) =>
      api<{ data: DomainContact }>(`${base(orgId)}/domains/contact`, { method: "PUT", body: input }),
    onSuccess: (result) => queryClient.setQueryData(keys.domainContact(orgId), result),
  });
}

/** A mutation rather than a query: each search is a registrar call, made on purpose. */
export function useCheckDomains(orgId: string) {
  return useMutation({
    mutationFn: (domains: string[]) =>
      api<{ data: DomainAvailability[] }>(`${base(orgId)}/domains/check`, { method: "POST", body: { domains } }),
  });
}

function invalidateDomains(queryClient: QueryClient, orgId: string): void {
  void queryClient.invalidateQueries({ queryKey: keys.registeredDomains(orgId) });
  // Every purchase is a charge, so the balance and the spend report move.
  void queryClient.invalidateQueries({ queryKey: keys.billing(orgId) });
  void queryClient.invalidateQueries({ queryKey: keys.spend(orgId) });
}

export function useRegisterDomain(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; extension: string; period: number; privateWhois: boolean; autorenew: boolean }) =>
      api<RegisteredDomain & { order: DomainOrder; warning: string | null }>(`${base(orgId)}/domains`, { method: "POST", body: input, idempotent: true }),
    onSuccess: (domain) => {
      track("domain_register", { object: "domain", id: domain.id });
      invalidateDomains(queryClient, orgId);
    },
  });
}

export function useTransferDomain(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; extension: string; authCode: string; privateWhois: boolean; autorenew: boolean }) =>
      api<RegisteredDomain & { order: DomainOrder; warning: string | null }>(`${base(orgId)}/domains/transfer`, { method: "POST", body: input, idempotent: true }),
    onSuccess: (domain) => {
      track("domain_transfer", { object: "domain", id: domain.id });
      invalidateDomains(queryClient, orgId);
    },
  });
}

export function useRenewDomain(orgId: string, domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (period: number) =>
      api<RegisteredDomain & { order: DomainOrder }>(`${base(orgId)}/domains/${domainId}/renew`, { method: "POST", body: { period }, idempotent: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.registeredDomain(orgId, domainId) });
      invalidateDomains(queryClient, orgId);
    },
  });
}

export function useUpdateDomain(orgId: string, domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { autorenew?: boolean; privateWhois?: boolean; locked?: boolean; nameServers?: string[] }) =>
      api<RegisteredDomain>(`${base(orgId)}/domains/${domainId}`, { method: "PATCH", body: patch }),
    onSuccess: (domain) => {
      queryClient.setQueryData(keys.registeredDomain(orgId, domainId), (old: RegisteredDomainDetail | undefined) =>
        old ? { ...old, ...domain } : old,
      );
      void queryClient.invalidateQueries({ queryKey: keys.registeredDomains(orgId) });
      // Nameservers moving changes whether the zone is edited here.
      void queryClient.invalidateQueries({ queryKey: keys.domainRecords(orgId, domainId) });
    },
  });
}

export function useSyncDomain(orgId: string, domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<RegisteredDomain>(`${base(orgId)}/domains/${domainId}/sync`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.registeredDomain(orgId, domainId) });
      void queryClient.invalidateQueries({ queryKey: keys.registeredDomains(orgId) });
    },
  });
}

/** Read on demand and never cached: the code is the domain, so the password comes with the request. */
export function useDomainAuthCode(orgId: string, domainId: string) {
  return useMutation({
    mutationFn: (password: string) =>
      api<{ data: { authCode: string } }>(`${base(orgId)}/domains/${domainId}/auth-code`, { method: "POST", body: { password } }),
  });
}

export function useZoneRecords(
  orgId: string | undefined,
  domainId: string | undefined,
  enabled = true,
): UseQueryResult<{ data: ZoneRecord[] }> {
  return useQuery({
    queryKey: keys.domainRecords(orgId ?? "", domainId ?? ""),
    queryFn: () => api<{ data: ZoneRecord[] }>(`${base(orgId!)}/domains/${domainId}/records`),
    enabled: Boolean(orgId && domainId) && enabled,
  });
}

export function useReplaceZoneRecords(orgId: string, domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (records: ZoneRecord[]) =>
      api<{ data: ZoneRecord[] }>(`${base(orgId)}/domains/${domainId}/records`, { method: "PUT", body: { records } }),
    onSuccess: (result) => queryClient.setQueryData(keys.domainRecords(orgId, domainId), result),
  });
}

export function usePointDomain(orgId: string, domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) =>
      api<{ data: { records: ZoneRecord[]; hosts: string[] } }>(`${base(orgId)}/domains/${domainId}/point`, {
        method: "POST",
        body: { appId },
      }),
    onSuccess: (_result, appId) => {
      void queryClient.invalidateQueries({ queryKey: keys.domainRecords(orgId, domainId) });
      // The detail now says where the name points.
      void queryClient.invalidateQueries({ queryKey: keys.registeredDomain(orgId, domainId) });
      // The app gained two custom domains.
      void queryClient.invalidateQueries({ queryKey: keys.domains(orgId, appId) });
    },
  });
}
